import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiException } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Spinner } from '../../components/ui/spinner';
import { Modal } from '../../components/ui/modal';
import { UserPlus, KeyRound, Power } from 'lucide-react';

interface Usuario {
  id: string; nome: string; sobrenome: string | null; email: string;
  status: string; roleCode: string | null; lastLoginAt: string | null;
}

const ROLES = [
  { code: 'ADMIN', label: 'Administrador (Master)' },
  { code: 'TESOUREIRO', label: 'Tesouraria' },
  { code: 'COORDENADOR', label: 'Coordenação' },
  { code: 'ANALISTA', label: 'Analista' },
  { code: 'AGENTE', label: 'Agente' },
];
const roleLabel: Record<string, string> = Object.fromEntries(ROLES.map((r) => [r.code, r.label]));
const statusLabel: Record<string, string> = { ativo: 'Ativo', inativo: 'Inativo', bloqueado: 'Bloqueado' };

export function UsuariosPage() {
  const qc = useQueryClient();
  const [novo, setNovo] = useState(false);
  const [resetando, setResetando] = useState<Usuario | null>(null);
  const [form, setForm] = useState({ nome: '', sobrenome: '', email: '', senha: '', roleCode: 'AGENTE' });
  const [novaSenha, setNovaSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['usuarios'], queryFn: () => api<{ items: Usuario[] }>('/usuarios') });

  const criar = useMutation({
    mutationFn: () => api('/usuarios', { method: 'POST', body: { ...form, sobrenome: form.sobrenome || undefined } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['usuarios'] }); setNovo(false); setForm({ nome: '', sobrenome: '', email: '', senha: '', roleCode: 'AGENTE' }); },
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao criar usuário'),
  });

  const toggleStatus = useMutation({
    mutationFn: (u: Usuario) => api(`/usuarios/${u.id}/status`, { method: 'PATCH', body: { status: u.status === 'ativo' ? 'inativo' : 'ativo' } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['usuarios'] }),
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao alterar status'),
  });

  const resetar = useMutation({
    mutationFn: () => api(`/usuarios/${resetando!.id}/senha`, { method: 'PATCH', body: { senha: novaSenha } }),
    onSuccess: () => { setResetando(null); setNovaSenha(''); },
    onError: (e) => setErro(e instanceof ApiException ? e.message : 'Erro ao resetar senha'),
  });

  if (isLoading) return <div className="flex justify-center p-12"><Spinner /></div>;

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Usuários</h1>
          <p className="text-sm text-muted-foreground">Gestão de acessos por papel</p>
        </div>
        <Button onClick={() => { setNovo(true); setErro(null); }}><UserPlus className="h-4 w-4 mr-1" /> Novo usuário</Button>
      </div>

      {erro && <div className="mb-4 rounded border border-red-300 bg-red-100 dark:bg-red-950 dark:border-red-800 p-3 text-sm text-red-800 dark:text-red-300">{erro} <button className="ml-2 underline" onClick={() => setErro(null)}>fechar</button></div>}

      <Card>
        <CardHeader><CardTitle>Equipe {data && <span className="text-sm font-normal text-muted-foreground">{data.items.length}</span>}</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-gray-500">
              <th className="py-2">Nome</th><th className="py-2">E-mail</th>
              <th className="py-2">Papel</th><th className="py-2">Status</th><th className="py-2 w-32"></th>
            </tr></thead>
            <tbody>
              {data?.items.map((u) => (
                <tr key={u.id} className="border-b">
                  <td className="py-2 font-medium">{u.nome}{u.sobrenome ? ` ${u.sobrenome}` : ''}</td>
                  <td className="py-2">{u.email}</td>
                  <td className="py-2">{u.roleCode ? roleLabel[u.roleCode] ?? u.roleCode : '—'}</td>
                  <td className="py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs ${u.status === 'ativo' ? 'bg-green-100 text-green-900' : 'bg-gray-200 text-gray-700'}`}>
                      {statusLabel[u.status] ?? u.status}
                    </span>
                  </td>
                  <td className="py-2 text-right">
                    <button className="text-gray-400 hover:text-blue-600 mr-3" title="Resetar senha" onClick={() => { setResetando(u); setErro(null); }}>
                      <KeyRound className="h-4 w-4 inline" />
                    </button>
                    <button className={`${u.status === 'ativo' ? 'text-gray-400 hover:text-red-600' : 'text-gray-400 hover:text-green-600'}`} title={u.status === 'ativo' ? 'Desativar' : 'Ativar'} onClick={() => toggleStatus.mutate(u)}>
                      <Power className="h-4 w-4 inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {novo && (
        <Modal open onClose={() => setNovo(false)} title="Novo usuário">
          <div className="space-y-3">
            {erro && <div className="rounded border border-red-300 bg-red-100 dark:bg-red-950 dark:border-red-800 p-2 text-sm text-red-800 dark:text-red-300">{erro}</div>}
            <div className="flex gap-2">
              <Input placeholder="Nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} />
              <Input placeholder="Sobrenome" value={form.sobrenome} onChange={(e) => setForm({ ...form, sobrenome: e.target.value })} />
            </div>
            <Input placeholder="E-mail" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Input placeholder="Senha (mín 8)" type="text" value={form.senha} onChange={(e) => setForm({ ...form, senha: e.target.value })} />
            <select className="w-full rounded border border-border bg-background text-foreground p-2 text-sm" value={form.roleCode} onChange={(e) => setForm({ ...form, roleCode: e.target.value })}>
              {ROLES.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setNovo(false)}>Cancelar</Button>
              <Button disabled={!form.nome || !form.email || form.senha.length < 8 || criar.isPending} onClick={() => criar.mutate()}>Criar</Button>
            </div>
          </div>
        </Modal>
      )}

      {resetando && (
        <Modal open onClose={() => setResetando(null)} title="Resetar senha">
          <div className="space-y-3">
            {erro && <div className="rounded border border-red-300 bg-red-100 dark:bg-red-950 dark:border-red-800 p-2 text-sm text-red-800 dark:text-red-300">{erro}</div>}
            <p className="text-sm">Nova senha para <strong>{resetando.email}</strong>:</p>
            <Input placeholder="Nova senha (mín 8)" type="text" value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setResetando(null)}>Cancelar</Button>
              <Button disabled={novaSenha.length < 8 || resetar.isPending} onClick={() => resetar.mutate()}>Resetar</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
